import { useMemo, useState } from "react";
import {
  Labeled,
  FunctionField,
  Confirm,
  useRecordContext,
  useResourceContext,
  useGetOne,
  useDelete,
} from "react-admin";
import {
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Box,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { CustomerNameField } from "../../components";

const metaRatings = {
  sparseFields: [
    "id",
    "rating",
    {
      ratings: ["id", "clientId", "providerId", "createdTimestamp", "rating"],
    },
  ],
};

const metaReviews = {
  sparseFields: [
    "id",
    {
      reviews: [
        "id",
        "clientId",
        "clientUserName",
        "createdTimestamp",
        "providerId",
        "review",
      ],
    },
  ],
};

const EditOtherRatingsReviews = () => {
  const [deleteDialogOpenFor, setDeleteDialogOpenFor] = useState(null);
  const record = useRecordContext();
  const resource = useResourceContext();
  const id = record.id;

  const {
    data: providerRatings,
    isLoading: isLoadingRatings,
    refetch: refetchRatings,
    error: errorGetRatings,
  } = useGetOne(resource, { id, meta: metaRatings });

  const {
    data: providerReviews,
    isLoading: isLoadingReviews,
    refetch: refetchReviews,
    error: errorGetReviews,
  } = useGetOne(resource, { id, meta: metaReviews });

  const otherReviews = useMemo(
    () =>
      (providerReviews?.reviews || []).filter(
        (item) =>
          !(providerRatings?.ratings || [])
            .map((r) => r.clientId)
            .includes(item.clientId) &&
          !(record?.references || []).map((r) => r.id).includes(item.clientId),
      ),
    [providerReviews, providerRatings, record],
  );

  const [deleteOne] = useDelete();

  const refetch = () => {
    refetchRatings();
    refetchReviews();
  };

  const handleDeleteReview = (reviewId) => {
    deleteOne(
      "Review",
      { id: reviewId, meta: { sparseFields: ["id"] } },
      {
        onSuccess: () => refetch(),
      },
    );
  };

  if (isLoadingRatings || isLoadingReviews) {
    return <div>Loading...</div>;
  }

  if (otherReviews?.length === 0) {
    return null;
  }

  if (errorGetRatings) {
    throw errorGetRatings;
  }

  if (errorGetReviews) {
    throw errorGetReviews;
  }

  return (
    <Labeled
      sx={{ width: "100%", marginTop: "16px" }}
      label="No reference or rating reviews"
    >
      <Stack sx={{ gap: "12px" }}>
        <Table>
          <TableBody>
            {otherReviews.map((item, index) => (
              <TableRow key={`rating-review-${index}`}>
                <TableCell sx={{ width: "auto", paddingLeft: 0 }}>
                  <Box
                    sx={{
                      display: "grid",
                      gap: "8px 32px",
                      gridTemplateColumns: "auto auto 1fr auto",
                    }}
                  >
                    <Labeled label="Customer Name">
                      <FunctionField
                        render={() => {
                          return Number.isFinite(parseInt(item.clientId)) ? (
                            <CustomerNameField clientId={item.clientId} />
                          ) : (
                            <span>{item.clientUserName || "-"}</span>
                          );
                        }}
                      />
                    </Labeled>

                    <Labeled label="Added on">
                      <FunctionField
                        render={() =>
                          item["createdTimestamp"]
                            ? new Date(+item["createdTimestamp"]).toDateString()
                            : "-"
                        }
                      />
                    </Labeled>
                    <Labeled
                      label="Review"
                      component="div"
                      sx={{ gridColumn: "span 3" }}
                    >
                      <FunctionField render={() => item["review"]} />
                    </Labeled>
                  </Box>
                </TableCell>
                <TableCell sx={{ width: "30px" }}>
                  <FunctionField
                    sx={{
                      gridRow: "1 / 3",
                      gridColumn: "4 / 5",
                      alignSelf: "center",
                    }}
                    render={() => (
                      <>
                        <IconButton
                          aria-label="delete"
                          onClick={() => setDeleteDialogOpenFor(item.id)}
                        >
                          <DeleteIcon />
                        </IconButton>

                        <Confirm
                          isOpen={deleteDialogOpenFor === item.id}
                          key={item.id}
                          title={`Delete item`}
                          content={`Are you sure you want to delete this item?`}
                          onConfirm={() => {
                            handleDeleteReview(item?.id);
                            setDeleteDialogOpenFor(null);
                          }}
                          onClose={() => setDeleteDialogOpenFor(null)}
                        />
                      </>
                    )}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Stack>
    </Labeled>
  );
};

export default EditOtherRatingsReviews;
